import { useState, useRef, useEffect } from "react";
import { Plus, Search, ChevronDown, X, RefreshCw, Edit2, Trash2, Check } from "lucide-react";
import clsx from "clsx";
import { useWatchlistsStore, type WatchlistSymbol } from "@/store/watchlists";
import { useQuotes } from "@/hooks/useQuotes";
import { SYMBOLS, type SymbolEntry } from "@/lib/symbols";
import { searchInstruments, toSymbolEntry, type Instrument } from "@/lib/instruments";
import { formatPrice, formatPercent } from "@/lib/format";

interface WatchlistPanelProps {
  /** Currently displayed chart symbol — highlighted in the list */
  currentSymbol: string;
  currentExchange: string;
  /** Called when user clicks a watchlist row */
  onSelect: (entry: SymbolEntry) => void;
}

export function WatchlistPanel({ currentSymbol, currentExchange, onSelect }: WatchlistPanelProps) {
  const { lists, activeId, setActive, createList, renameList, deleteList, addSymbol, removeSymbol } =
    useWatchlistsStore();

  const activeList = lists.find((l) => l.id === activeId) ?? lists[0];

  const [listMenuOpen, setListMenuOpen] = useState(false);
  const [renameDraft, setRenameDraft] = useState<string | null>(null);
  const [addSymbolOpen, setAddSymbolOpen] = useState(false);
  const listMenuRef = useRef<HTMLDivElement>(null);

  // Close list dropdown on outside click
  useEffect(() => {
    if (!listMenuOpen) return;
    const h = (e: MouseEvent) => {
      if (listMenuRef.current && !listMenuRef.current.contains(e.target as Node)) {
        setListMenuOpen(false);
        setRenameDraft(null);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [listMenuOpen]);

  const { quotes, refetch } = useQuotes({
    symbols: activeList?.symbols ?? [],
    refreshMs: 0,
  });

  if (!activeList) {
    return (
      <div className="p-4 text-center text-2xs text-text-muted">
        No watchlists. Create one to get started.
        <button
          onClick={() => createList("My watchlist")}
          className="mt-3 w-full rounded-md bg-accent px-3 py-1.5 text-xs text-white hover:bg-accent-hover"
        >
          Create watchlist
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Top bar: list selector + actions */}
      <div className="flex items-center gap-1 border-b border-border bg-bg-panel px-2 py-1.5">
        <div ref={listMenuRef} className="relative flex-1">
          <button
            onClick={() => setListMenuOpen((v) => !v)}
            className="flex w-full items-center gap-1.5 rounded px-2 py-1 text-left text-xs font-semibold text-text-primary hover:bg-bg-elevated"
          >
            <span className="truncate">{activeList.name}</span>
            <ChevronDown className="h-3 w-3 shrink-0 text-text-muted" />
          </button>

          {listMenuOpen && (
            <div className="absolute left-0 right-0 top-full z-30 mt-1 rounded-md border border-border bg-bg-elevated shadow-elevated animate-slide-up">
              {lists.map((l) => (
                <ListRow
                  key={l.id}
                  list={l}
                  isActive={l.id === activeList.id}
                  isRenaming={renameDraft !== null && l.id === activeList.id}
                  draft={renameDraft ?? ""}
                  setDraft={setRenameDraft}
                  onClick={() => {
                    setActive(l.id);
                    setListMenuOpen(false);
                  }}
                  onRename={() => setRenameDraft(l.name)}
                  onCommitRename={(name) => {
                    renameList(l.id, name.trim() || l.name);
                    setRenameDraft(null);
                  }}
                  onDelete={() => {
                    if (lists.length === 1) {
                      alert("Can't delete the last watchlist.");
                      return;
                    }
                    if (confirm(`Delete watchlist "${l.name}"?`)) {
                      deleteList(l.id);
                      setListMenuOpen(false);
                    }
                  }}
                />
              ))}
              <button
                onClick={() => {
                  const name = prompt("New watchlist name:", "New list");
                  if (name) {
                    createList(name);
                    setListMenuOpen(false);
                  }
                }}
                className="flex w-full items-center gap-1.5 border-t border-border px-3 py-1.5 text-xs text-text-secondary transition-colors hover:bg-bg-hover hover:text-text-primary"
              >
                <Plus className="h-3.5 w-3.5" />
                New watchlist
              </button>
            </div>
          )}
        </div>

        <button
          onClick={() => setAddSymbolOpen(true)}
          className="rounded p-1 text-text-secondary hover:bg-bg-elevated hover:text-text-primary"
          title="Add symbol"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={refetch}
          className="rounded p-1 text-text-secondary hover:bg-bg-elevated hover:text-text-primary"
          title="Refresh prices"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Add-symbol overlay */}
      {addSymbolOpen && (
        <AddSymbolDialog
          existing={activeList.symbols}
          onAdd={(sym) => addSymbol(activeList.id, sym)}
          onClose={() => setAddSymbolOpen(false)}
        />
      )}

      {/* Symbol list */}
      <div className="flex-1 overflow-y-auto">
        {activeList.symbols.length === 0 ? (
          <div className="px-3 py-6 text-center text-2xs text-text-muted">
            Empty watchlist. Click + to add symbols.
          </div>
        ) : (
          activeList.symbols.map((s) => {
            const key = `${s.symbol}:${s.exchange}`;
            const q = quotes[key];
            const isCurrent = s.symbol === currentSymbol && s.exchange === currentExchange;
            const isUp = q && q.change >= 0;

            return (
              <button
                key={key}
                onClick={() => {
                  const known = SYMBOLS.find((k) => k.symbol === s.symbol && k.exchange === s.exchange);
                  onSelect(known ?? {
                    symbol: s.symbol,
                    exchange: s.exchange,
                    name: s.name ?? s.symbol,
                    category: "US Equity", // fallback category
                  });
                }}
                className={clsx(
                  "group flex w-full items-center gap-2 border-b border-border/40 px-3 py-2 text-left transition-colors",
                  isCurrent
                    ? "bg-accent/10 hover:bg-accent/15"
                    : "hover:bg-bg-elevated"
                )}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className={clsx(
                      "font-mono text-xs font-semibold truncate",
                      isCurrent ? "text-accent" : "text-text-primary"
                    )}>
                      {s.symbol}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider text-text-muted">
                      {s.exchange}
                    </span>
                  </div>
                  {s.name && (
                    <div className="truncate text-[10px] text-text-muted">{s.name}</div>
                  )}
                </div>

                <div className="flex flex-col items-end font-mono tabular text-xs shrink-0">
                  {q?.loading ? (
                    <span className="text-text-muted">…</span>
                  ) : q?.last !== null && q?.last !== undefined ? (
                    <>
                      <span className="text-text-primary">{formatPrice(q.last)}</span>
                      <span className={clsx("text-[10px]", isUp ? "text-bull" : "text-bear")}>
                        {formatPercent(q.changePct)}
                      </span>
                    </>
                  ) : (
                    <span className="text-text-muted">—</span>
                  )}
                </div>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeSymbol(activeList.id, s.symbol, s.exchange);
                  }}
                  className="ml-1 opacity-0 group-hover:opacity-100 text-text-muted hover:text-bear transition-opacity"
                  title="Remove"
                >
                  <X className="h-3 w-3" />
                </button>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

function ListRow({
  list, isActive, isRenaming, draft, setDraft, onClick, onRename, onCommitRename, onDelete,
}: {
  list: { id: string; name: string; symbols: WatchlistSymbol[] };
  isActive: boolean;
  isRenaming: boolean;
  draft: string;
  setDraft: (v: string | null) => void;
  onClick: () => void;
  onRename: () => void;
  onCommitRename: (name: string) => void;
  onDelete: () => void;
}) {
  if (isRenaming) {
    return (
      <div className="flex items-center gap-1 px-3 py-1.5">
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onCommitRename(draft);
            if (e.key === "Escape") setDraft(null);
          }}
          className="flex-1 rounded bg-bg-panel px-2 py-1 text-xs text-text-primary focus:outline-none"
        />
        <button onClick={() => onCommitRename(draft)} className="text-bull hover:text-bull/80">
          <Check className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }
  return (
    <div className={clsx("group flex items-center gap-1 px-3 py-1.5 hover:bg-bg-hover", isActive && "bg-accent/10")}>
      <button onClick={onClick} className="flex-1 truncate text-left text-xs text-text-primary">
        {list.name}
        <span className="ml-1.5 text-[10px] text-text-muted">({list.symbols.length})</span>
      </button>
      <button onClick={onRename} className="opacity-0 group-hover:opacity-100 text-text-muted hover:text-text-primary" title="Rename">
        <Edit2 className="h-3 w-3" />
      </button>
      <button onClick={onDelete} className="opacity-0 group-hover:opacity-100 text-text-muted hover:text-bear" title="Delete">
        <Trash2 className="h-3 w-3" />
      </button>
    </div>
  );
}

function AddSymbolDialog({
  existing, onAdd, onClose,
}: {
  existing: WatchlistSymbol[];
  onAdd: (sym: WatchlistSymbol) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const q = query.trim().toUpperCase();
  const existingKeys = new Set(existing.map((s) => `${s.symbol}:${s.exchange}`));

  // Server search against the archive -- the curated list cannot cover
  // option strikes or futures, which is most of what gets watched here.
  const [remote, setRemote] = useState<Instrument[]>([]);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!q) {
      setRemote([]);
      return;
    }
    const ctrl = new AbortController();
    abortRef.current?.abort();
    abortRef.current = ctrl;
    const t = setTimeout(() => {
      searchInstruments(q, ctrl.signal)
        .then(setRemote)
        .catch(() => setRemote([]));
    }, 250);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [q]);

  const local = SYMBOLS.filter((s) => {
    if (!q) return true;
    return s.symbol.includes(q) || s.name.toUpperCase().includes(q) || s.exchange.includes(q);
  });

  const seenRemote = new Set(remote.map((r) => `${r.symbol}:${r.exchange}`));
  const matches: SymbolEntry[] = [
    ...remote.map(toSymbolEntry),
    ...local.filter((s) => !seenRemote.has(`${s.symbol}:${s.exchange}`)),
  ].filter((s) => !existingKeys.has(`${s.symbol}:${s.exchange}`));

  return (
    <div className="absolute inset-0 z-30 flex flex-col bg-bg-panel">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <Search className="h-3.5 w-3.5 text-text-muted" />
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search to add (or type custom)…"
          className="flex-1 bg-transparent text-sm text-text-primary placeholder:text-text-muted focus:outline-none"
        />
        <button onClick={onClose} className="text-text-muted hover:text-text-primary">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {matches.length === 0 && !q && (
          <div className="px-3 py-4 text-center text-2xs text-text-muted">All catalog symbols already added.</div>
        )}
        {matches.length === 0 && q && (
          <div className="space-y-2 px-3 py-3 text-2xs text-text-muted">
            <div>No match for "{q}".</div>
            <button
              onClick={() => {
                const exchange = prompt('Exchange (e.g. NSE, NASDAQ, INDEX):', 'NASDAQ');
                if (!exchange) return;
                onAdd({ symbol: q, exchange: exchange.toUpperCase(), name: q });
                onClose();
              }}
              className="rounded bg-accent px-2 py-1 text-xs text-white hover:bg-accent-hover"
            >
              Add "{q}" as custom symbol
            </button>
          </div>
        )}
        {matches.map((s) => (
          <button
            key={`${s.exchange}:${s.symbol}`}
            onClick={() => {
              onAdd({ symbol: s.symbol, exchange: s.exchange, name: s.name });
              setQuery("");
            }}
            className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-bg-hover"
          >
            <div className="flex items-baseline gap-2">
              <span className="font-mono text-xs font-semibold text-text-primary">{s.symbol}</span>
              <span className="text-2xs text-text-secondary">{s.name}</span>
            </div>
            <span className="text-2xs uppercase tracking-wider text-text-muted">{s.exchange}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
